/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  ACTIVITIES_EVENTS_FILTER_VALUE_ADD,
  ACTIVITIES_EVENTS_FILTER_VALUE_BULK_REMOVE,
  ACTIVITIES_EVENTS_FILTER_VALUE_REMOVE,
  ACTIVITIES_EVENTS_FILTER_VALUE_RESET,
  CLEAR_APPLICATION_DETAILS_STATE,
  GET_ACTIVITY_EVENT_BY_ID,
  GET_ACTIVITY_EVENT_BY_ID_FAIL,
  GET_ACTIVITY_EVENT_BY_ID_SUCCESS,
  GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID,
  GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID_FAIL,
  GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID_SUCCESS,
  GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID,
  GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID_FAIL,
  GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID_SUCCESS,
  GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID,
  GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID_FAIL,
  GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID_SUCCESS,
  GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID,
  GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID_FAIL,
  GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID_SUCCESS,
  GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID,
  GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID_FAIL,
  GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID_SUCCESS,
  UPDATE_ACTIVITIES_EVENTS_CURRENT_PAGE,
  UPDATE_ACTIVITIES_EVENTS_PAGE_SIZE,
  UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS,
  UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS_FAIL,
  UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS_RESET,
  UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS_SUCCESS,
  UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES,
  UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES_FAIL,
  UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES_RESET,
  UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES_SUCCESS,
} from "Dataflows/ApplicationDetails/ApplicationDetailsActionTypes";
import { IApplicationFlagResponse } from "Interfaces/Application/IApplicationFlagResponse";
import { IApplicationFormActivityEvent } from "Interfaces/Application/IApplicationFormActivityEvent";
import { IApplicationFormActivityEventList } from "Interfaces/Application/IApplicationFormActivityEventList";
import { IApplicationFormTechnologyFocusAreaStatusUpdateData } from "Interfaces/Application/IApplicationFormTechnologyFocusAreaStatusUpdateData";
import { IApplicationFormTechnologyFocusAreaUpdateData } from "Interfaces/Application/IApplicationFormTechnologyFocusAreaUpdateData";
import { IApplicationQuestionResponse } from "Interfaces/Application/IApplicationQuestionResponse";
import { IApplicationUserProfileInformation } from "Interfaces/Application/IApplicationUserProfileInformation";
import { IFilterValue } from "Interfaces/IFilterValue";

/**
 * Action for when adding selected filter value.
 * @interface
 */
export interface IActivitiesEventsFilterValueAdd {
  type: typeof ACTIVITIES_EVENTS_FILTER_VALUE_ADD;
  filterValue: IFilterValue;
  isHighlighted: boolean;
}

/**
 * Action for when removing selected filter value.
 * @interface
 */
export interface IActivitiesEventsFilterValueRemove {
  type: typeof ACTIVITIES_EVENTS_FILTER_VALUE_REMOVE;
  filterValue: IFilterValue;
  isHighlighted: boolean;
}

/**
 * Action for when removing selected filter value.
 * @interface
 */
export interface IActivitiesEventsFilterValueBulkRemove {
  type: typeof ACTIVITIES_EVENTS_FILTER_VALUE_BULK_REMOVE;
  filterValues: IFilterValue[];
  isHighlighted: boolean;
}

export interface IUpdateActivitiesEventsCurrentPage {
  type: typeof UPDATE_ACTIVITIES_EVENTS_CURRENT_PAGE;
  currentPage: number;
  isHighlighted: boolean;
}

export interface IUpdateActivitiesEventsPageSize {
  type: typeof UPDATE_ACTIVITIES_EVENTS_PAGE_SIZE;
  pageSize: number;
  isHighlighted: boolean;
}

/**
 * Action for when resetting selected filter value.
 * @interface
 */
export interface IActivitiesEventsFilterValueReset {
  type: typeof ACTIVITIES_EVENTS_FILTER_VALUE_RESET;
  isHighlighted: boolean;
}

/**
 * Action for when getting the selected application by id.
 * @interface
 */
export interface IGetUserProfileBySelectedApplicationId {
  type: typeof GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID;
  selectedApplicationId: number;
  tenant: string;
}

/**
 * Action for when getting the selected application by id success.
 * @interface
 */
export interface IGetUserProfileBySelectedApplicationIdSuccess {
  type: typeof GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID_SUCCESS;
  userProfileInfo: IApplicationUserProfileInformation;
}

/**
 * Action for when getting the selected application by id fail.
 * @interface
 */
export interface IGetUserProfileBySelectedApplicationIdFail {
  type: typeof GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID_FAIL;
  errorMessage: string;
}

export interface IGetApplicationFlagsBySelectedApplicationId {
  type: typeof GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID;
  selectedApplicationId: number;
}

export interface IGetApplicationFlagsBySelectedApplicationIdSuccess {
  type: typeof GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID_SUCCESS;
  applicationFlags: IApplicationFlagResponse[];
}

export interface IGetApplicationFlagsBySelectedApplicationIdFail {
  type: typeof GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID_FAIL;
  errorMessage: string;
}

export interface IGetApplicationQuestionsBySelectedApplicationId {
  type: typeof GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID;
  selectedApplicationId: number;
  tenant: string;
}

export interface IGetApplicationQuestionsBySelectedApplicationIdSuccess {
  type: typeof GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID_SUCCESS;
  applicationQuestions: IApplicationQuestionResponse[];
}

export interface IGetApplicationQuestionsBySelectedApplicationIdFail {
  type: typeof GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID_FAIL;
  errorMessage: string;
}

export interface IGetHighlightedActivitiesEventsBySelectedApplicationId {
  type: typeof GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID;
  applicationId: number;
  pageIndex: string;
  pageSize: string;
  selectedFilterValues: IFilterValue[];
  tenant: string;
  sortFieldName?: string;
  isDescendingSort?: boolean;
}

export interface IGetHighlightedActivitiesEventsBySelectedApplicationIdSuccess {
  type: typeof GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID_SUCCESS;
  highlightedActivitiesEvents: IApplicationFormActivityEventList;
}

export interface IGetHighlightedActivitiesEventsBySelectedApplicationIdFail {
  type: typeof GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID_FAIL;
  errorMessage: string;
}

export interface IGetAdditionalActivitiesEventsBySelectedUserprofileId {
  type: typeof GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID;
  userprofileId: number;
  pageIndex: string;
  pageSize: string;
  selectedFilterValues: IFilterValue[];
  tenant: string;
  sortFieldName?: string;
  isDescendingSort?: boolean;
}

export interface IGetAdditionalActivitiesEventsBySelectedUserprofileIdFail {
  type: typeof GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID_FAIL;
  errorMessage: string;
}

export interface IGetAdditionalActivitiesEventsBySelectedUserprofileIdSuccess {
  type: typeof GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID_SUCCESS;
  additionalActivitiesEvents: IApplicationFormActivityEventList;
}

export interface IClearApplicationDetailsState {
  type: typeof CLEAR_APPLICATION_DETAILS_STATE;
}

export interface IGetActivityEvent {
  type: typeof GET_ACTIVITY_EVENT_BY_ID;
  activityEventId: number;
  contributionType: string;
}

export interface IGetActivityEventSuccess {
  type: typeof GET_ACTIVITY_EVENT_BY_ID_SUCCESS;
  activityEvent: IApplicationFormActivityEvent;
}

export interface IGetActivityEventFail {
  type: typeof GET_ACTIVITY_EVENT_BY_ID_FAIL;
  errorMessage: string;
}

export interface IUpdateApplicationStatusesAndRemoveFlags {
  type: typeof UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS;
  applicationFormId: number;
  technologyFocusAreaStatuses: IApplicationFormTechnologyFocusAreaStatusUpdateData[];
  removeFlags: boolean;
  justification: string;
}

export interface IUpdateApplicationStatusesAndRemoveFlagsFail {
  type: typeof UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS_FAIL;
  errorMessage: string;
}

export interface IUpdateApplicationStatusesAndRemoveFlagsSuccess {
  type: typeof UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS_SUCCESS;
}

export interface IUpdateApplicationStatusesAndRemoveFlagsReset {
  type: typeof UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS_RESET;
}

export interface IUpdateApplicationTechnologyAreaAndResetScores {
  type: typeof UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES;
  applicationFormId: number;
  technologyFocusAreas: IApplicationFormTechnologyFocusAreaUpdateData[];
  justification: string;
}

export interface IUpdateApplicationTechnologyAreaAndResetScoresFail {
  type: typeof UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES_FAIL;
  errorMessage: string;
}

export interface IUpdateApplicationTechnologyAreaAndResetScoresSuccess {
  type: typeof UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES_SUCCESS;
}

export interface IUpdateApplicationTechnologyAreaAndResetScoresReset {
  type: typeof UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES_RESET;
}

export function addActivitiesEventsFilterValue(
  filterValue: IFilterValue,
  isHighlighted: boolean
): IActivitiesEventsFilterValueAdd {
  return {
    type: ACTIVITIES_EVENTS_FILTER_VALUE_ADD,
    filterValue,
    isHighlighted,
  };
}

export function removeActivitiesEventsFilterValue(
  filterValue: IFilterValue,
  isHighlighted: boolean
): IActivitiesEventsFilterValueRemove {
  return {
    type: ACTIVITIES_EVENTS_FILTER_VALUE_REMOVE,
    filterValue,
    isHighlighted,
  };
}

export function removeMultipleActivitiesEventsFilterValues(
  filterValues: IFilterValue[],
  isHighlighted: boolean
): IActivitiesEventsFilterValueBulkRemove {
  return {
    type: ACTIVITIES_EVENTS_FILTER_VALUE_BULK_REMOVE,
    filterValues,
    isHighlighted,
  };
}

export function updateActivitiesEventsCurrentPage(
  currentPage: number,
  isHighlighted: boolean
): IUpdateActivitiesEventsCurrentPage {
  return {
    type: UPDATE_ACTIVITIES_EVENTS_CURRENT_PAGE,
    currentPage,
    isHighlighted,
  };
}

export function updateActivitiesEventsPageSize(
  pageSize: number,
  isHighlighted: boolean
): IUpdateActivitiesEventsPageSize {
  return {
    type: UPDATE_ACTIVITIES_EVENTS_PAGE_SIZE,
    pageSize,
    isHighlighted,
  };
}

/**
 * Action creator for reseting selected filter value.
 * @param isHighlighted
 * @returns {IActivitiesEventsFilterValueReset}
 */
export function resetActivitiesEventsFilterValues(
  isHighlighted: boolean
): IActivitiesEventsFilterValueReset {
  return {
    type: ACTIVITIES_EVENTS_FILTER_VALUE_RESET,
    isHighlighted,
  };
}

/**
 * Action creator for GET_SELECTED_APPLICATION_BY_ID.
 */
export function getUserProfileBySelectedApplicationId(
  selectedApplicationId: number,
  tenant: string
): ActivitiesEventsActionTypes {
  return {
    type: GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID,
    selectedApplicationId,
    tenant,
  };
}

/**
 * Action creator for GET_SELECTED_APPLICATION_BY_ID_SUCCESS.
 */
export function getUserProfileBySelectedApplicationIdSuccess(
  userProfileInfo: IApplicationUserProfileInformation
): ActivitiesEventsActionTypes {
  return {
    type: GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID_SUCCESS,
    userProfileInfo,
  };
}
/**
 * Action creator for GET_SELECTED_APPLICATION_BY_ID_FAIL.
 */
export function getUserProfileBySelectedApplicationIdFail(
  errorMessage: string
): ActivitiesEventsActionTypes {
  return {
    type: GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID_FAIL,
    errorMessage,
  };
}

export function fetchApplicationQuestionsBySelectedApplicationId(
  selectedApplicationId: number,
  tenant: string
): ActivitiesEventsActionTypes {
  return {
    type: GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID,
    selectedApplicationId,
    tenant,
  };
}

export function getApplicationQuestionsBySelectedApplicationIdSuccess(
  applicationQuestions: IApplicationQuestionResponse[]
): ActivitiesEventsActionTypes {
  return {
    type: GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID_SUCCESS,
    applicationQuestions,
  };
}

export function getApplicationQuestionsBySelectedApplicationIdFail(
  errorMessage: string
): ActivitiesEventsActionTypes {
  return {
    type: GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID_FAIL,
    errorMessage,
  };
}

export function fetchHighlightedActivitiesEventsBySelectedApplicationId(
  applicationId: number,
  pageIndex: string,
  pageSize: string,
  selectedFilterValues: IFilterValue[],
  tenant: string,
  sortFieldName?: string,
  isDescendingSort?: boolean
): ActivitiesEventsActionTypes {
  return {
    type: GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID,
    applicationId,
    pageIndex,
    pageSize,
    selectedFilterValues,
    tenant,
    sortFieldName,
    isDescendingSort,
  };
}

export function getHighlightedActivitiesEventsBySelectedApplicationIdSuccess(
  highlightedActivitiesEvents: IApplicationFormActivityEventList
): ActivitiesEventsActionTypes {
  return {
    type: GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID_SUCCESS,
    highlightedActivitiesEvents,
  };
}

export function getHighlightedActivitiesEventsBySelectedApplicationIdFail(
  errorMessage: string
): ActivitiesEventsActionTypes {
  return {
    type: GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID_FAIL,
    errorMessage,
  };
}

export function getAdditionalActivitiesEventsBySelectedUserProfileIdSuccess(
  additionalActivitiesEvents: IApplicationFormActivityEventList
): ActivitiesEventsActionTypes {
  return {
    type: GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID_SUCCESS,
    additionalActivitiesEvents,
  };
}

export function getAdditionalActivitiesEventsBySelectedUserProfileIdFail(
  errorMessage: string
): ActivitiesEventsActionTypes {
  return {
    type: GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID_FAIL,
    errorMessage,
  };
}

export function fetchAdditionalActivitiesEventsBySelectedUserprofileId(
  userprofileId: number,
  pageIndex: string,
  pageSize: string,
  selectedFilterValues: IFilterValue[],
  tenant: string,
  sortFieldName?: string,
  isDescendingSort?: boolean
): ActivitiesEventsActionTypes {
  return {
    type: GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID,
    userprofileId,
    pageIndex,
    pageSize,
    selectedFilterValues,
    tenant,
    sortFieldName,
    isDescendingSort,
  };
}

export function fetchActivityEventById(
  activityEventId: number,
  contributionType: string
): ActivitiesEventsActionTypes {
  return {
    type: GET_ACTIVITY_EVENT_BY_ID,
    activityEventId,
    contributionType,
  };
}

export function getActivityEventByIdSuccess(
  activityEvent: IApplicationFormActivityEvent
): ActivitiesEventsActionTypes {
  return {
    type: GET_ACTIVITY_EVENT_BY_ID_SUCCESS,
    activityEvent,
  };
}

export function getActivityEventByIdFail(errorMessage: string): ActivitiesEventsActionTypes {
  return {
    type: GET_ACTIVITY_EVENT_BY_ID_FAIL,
    errorMessage,
  };
}

export function clearApplicationDetailsState(): ActivitiesEventsActionTypes {
  return {
    type: CLEAR_APPLICATION_DETAILS_STATE,
  };
}

export function fetchApplicationFlagsBySelectedApplicationId(
  selectedApplicationId: number
): ActivitiesEventsActionTypes {
  return {
    type: GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID,
    selectedApplicationId,
  };
}

export function fetchApplicationFlagsBySelectedApplicationIdSuccess(
  applicationFlags: IApplicationFlagResponse[]
): ActivitiesEventsActionTypes {
  return {
    type: GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID_SUCCESS,
    applicationFlags,
  };
}

export function fetchApplicationFlagsBySelectedApplicationIdFail(
  errorMessage: string
): ActivitiesEventsActionTypes {
  return {
    type: GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID_FAIL,
    errorMessage,
  };
}

export function updateApplicationStatusesAndRemoveFlags(
  applicationFormId: number,
  technologyFocusAreaStatuses: IApplicationFormTechnologyFocusAreaStatusUpdateData[],
  removeFlags: boolean,
  justification: string
): ActivitiesEventsActionTypes {
  return {
    type: UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS,
    applicationFormId,
    technologyFocusAreaStatuses,
    removeFlags,
    justification,
  };
}

export function updateApplicationStatusesAndRemoveFlagsFail(
  errorMessage: string
): ActivitiesEventsActionTypes {
  return {
    type: UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS_FAIL,
    errorMessage,
  };
}

export function updateApplicationStatusesAndRemoveFlagsSuccess(): ActivitiesEventsActionTypes {
  return {
    type: UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS_SUCCESS,
  };
}

export function updateApplicationStatusesAndRemoveFlagsReset(): ActivitiesEventsActionTypes {
  return {
    type: UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS_RESET,
  };
}

export function updateApplicationTechnologyAreaAndResetScores(
  applicationFormId: number,
  technologyFocusAreas: IApplicationFormTechnologyFocusAreaUpdateData[],
  justification: string
): ActivitiesEventsActionTypes {
  return {
    type: UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES,
    applicationFormId,
    technologyFocusAreas,
    justification,
  };
}

export function updateApplicationTechnologyAreaAndResetScoresFail(
  errorMessage: string
): ActivitiesEventsActionTypes {
  return {
    type: UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES_FAIL,
    errorMessage,
  };
}

export function updateApplicationTechnologyAreaAndResetScoresSuccess(): ActivitiesEventsActionTypes {
  return {
    type: UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES_SUCCESS,
  };
}

export function updateApplicationTechnologyAreaAndResetScoresReset(): ActivitiesEventsActionTypes {
  return {
    type: UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES_RESET,
  };
}

export type ActivitiesEventsActionTypes =
  | IGetUserProfileBySelectedApplicationId
  | IGetUserProfileBySelectedApplicationIdFail
  | IGetUserProfileBySelectedApplicationIdSuccess
  | IGetApplicationFlagsBySelectedApplicationId
  | IGetApplicationFlagsBySelectedApplicationIdFail
  | IGetApplicationFlagsBySelectedApplicationIdSuccess
  | IGetApplicationQuestionsBySelectedApplicationId
  | IGetApplicationQuestionsBySelectedApplicationIdFail
  | IGetApplicationQuestionsBySelectedApplicationIdSuccess
  | IGetAdditionalActivitiesEventsBySelectedUserprofileId
  | IGetAdditionalActivitiesEventsBySelectedUserprofileIdFail
  | IGetAdditionalActivitiesEventsBySelectedUserprofileIdSuccess
  | IGetHighlightedActivitiesEventsBySelectedApplicationId
  | IGetHighlightedActivitiesEventsBySelectedApplicationIdFail
  | IGetHighlightedActivitiesEventsBySelectedApplicationIdSuccess
  | IGetActivityEvent
  | IGetActivityEventFail
  | IGetActivityEventSuccess
  | IActivitiesEventsFilterValueAdd
  | IActivitiesEventsFilterValueRemove
  | IActivitiesEventsFilterValueBulkRemove
  | IUpdateActivitiesEventsCurrentPage
  | IUpdateActivitiesEventsPageSize
  | IClearApplicationDetailsState
  | IUpdateApplicationStatusesAndRemoveFlags
  | IUpdateApplicationStatusesAndRemoveFlagsFail
  | IUpdateApplicationStatusesAndRemoveFlagsSuccess
  | IUpdateApplicationStatusesAndRemoveFlagsReset
  | IUpdateApplicationTechnologyAreaAndResetScores
  | IUpdateApplicationTechnologyAreaAndResetScoresFail
  | IUpdateApplicationTechnologyAreaAndResetScoresSuccess
  | IUpdateApplicationTechnologyAreaAndResetScoresReset
  | IActivitiesEventsFilterValueReset;
